import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anon, obj = authed
  const [error, setError] = useState(null);

  const refreshMe = useCallback(async () => {
    // Don't probe /me on the public login route — avoids noisy 401s in console
    if (typeof window !== "undefined" && window.location.pathname === "/login") {
      setUser(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // try refresh
      if (e?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setUser(data);
          return data;
        } catch {
          setUser(null);
          return null;
        }
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async ({ email, password, telemetry }) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password, telemetry });
      if (data?.mfa_required) {
        return { ...data, _stage: "mfa" };
      }
      setUser(data);
      return { ...data, _stage: "authed" };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const verifyMfa = useCallback(async ({ challenge_token, code, telemetry }) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/mfa/verify", { challenge_token, code, telemetry });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, error, login, verifyMfa, logout, refreshMe, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
